// Sidebar menu primitives — list, item, button, action, badge,
// skeleton, and the sub-menu trio. The Tailwind classes are shared
// constants so identical strings stay in lockstep.

import * as React from 'react'
import { Slot } from '@/components/ui/slot'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '../../../../src/cn'
import { useSidebar } from './use-sidebar'

const SIDEBAR_MENU_ACTION_CLASS = 'absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent-soft hover:text-sidebar-accent-soft-foreground focus-visible:ring-2 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:sm:h-4 [&>svg]:sm:w-4 [&>svg]:md:h-5 [&>svg]:md:w-5 [&>svg]:lg:h-5 [&>svg]:lg:w-5 [&>svg]:xl:h-6 [&>svg]:xl:w-6 [&>svg]:shrink-0 [&>svg]:transition-all [&>svg]:duration-200'

const SIDEBAR_MENU_BADGE_CLASS = 'absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none'

const SIDEBAR_MENU_BUTTON_CLASS = 'peer/menu-button flex w-full items-start gap-2 overflow-visible rounded-md py-2 sm:py-3 text-left text-sm outline-none ring-sidebar-ring transition-all hover:bg-sidebar-accent-soft hover:text-sidebar-accent-soft-foreground focus-visible:ring-2 focus-visible:ring-offset-2 active:bg-sidebar-accent-soft active:text-sidebar-accent-soft-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 group-has-[[data-sidebar=menu-badge]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent-soft data-[state=open]:hover:text-sidebar-accent-soft-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:!rounded-full [&>a]:flex [&>a]:w-full [&>a]:items-start [&>a]:gap-2 [&>a]:min-w-0 [&>a>span[data-sidebar-text]]:break-words [&>a>span[data-sidebar-text]]:whitespace-normal [&>a>span[data-sidebar-text]]:flex-1 [&>a>span[data-sidebar-text]]:min-w-0 [&>span:last-child]:break-words [&>span:last-child]:whitespace-normal [&>span:last-child]:flex-1 [&>span:last-child]:min-w-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:sm:h-4 [&>svg]:sm:w-4 [&>svg]:md:h-5 [&>svg]:md:w-5 [&>svg]:lg:h-5 [&>svg]:lg:w-5 [&>svg]:xl:h-6 [&>svg]:xl:w-6 [&>svg]:shrink-0 [&>svg]:transition-all [&>svg]:duration-200 [&>svg]:mt-0.5'

const SIDEBAR_MENU_SUB_BUTTON_CLASS = 'flex w-full items-start gap-2 overflow-visible rounded-md p-2 sm:p-3 text-left text-sm outline-none ring-sidebar-ring transition-all hover:bg-sidebar-accent-soft hover:text-sidebar-accent-soft-foreground focus-visible:ring-2 focus-visible:ring-offset-2 active:bg-sidebar-accent-soft active:text-sidebar-accent-soft-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>a]:flex [&>a]:w-full [&>a]:items-start [&>a]:gap-2 [&>a]:min-w-0 [&>a>span[data-sidebar-text]]:break-words [&>a>span[data-sidebar-text]]:whitespace-normal [&>a>span[data-sidebar-text]]:flex-1 [&>a>span[data-sidebar-text]]:min-w-0 [&>span:last-child]:break-words [&>span:last-child]:whitespace-normal [&>span:last-child]:flex-1 [&>span:last-child]:min-w-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:sm:h-4 [&>svg]:sm:w-4 [&>svg]:md:h-5 [&>svg]:md:w-5 [&>svg]:lg:h-5 [&>svg]:lg:w-5 [&>svg]:xl:h-6 [&>svg]:xl:w-6 [&>svg]:shrink-0 [&>svg]:transition-all [&>svg]:duration-200 [&>svg]:mt-0.5'

export const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      role="menu"
      className={cn('flex w-full min-w-0 flex-col gap-1 sm:gap-2', className)}
      {...props}
    />
  ),
)
SidebarMenu.displayName = 'SidebarMenu'

export const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      role="menuitem"
      className={cn('group/menu-item relative min-w-0', className)}
      {...props}
    />
  ),
)
SidebarMenuItem.displayName = 'SidebarMenuItem'

export const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean; showOnHover?: boolean }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      ref={ref}
      className={cn(
        SIDEBAR_MENU_ACTION_CLASS,
        showOnHover && 'group-hover/menu-item:opacity-100 group-data-[collapsible=icon]:hidden lg:opacity-0',
        className,
      )}
      {...props}
    />
  )
})
SidebarMenuAction.displayName = 'SidebarMenuAction'

export const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(SIDEBAR_MENU_BADGE_CLASS, className)} {...props} />
  ),
)
SidebarMenuBadge.displayName = 'SidebarMenuBadge'

export const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean
    isActive?: boolean
    tooltip?: string | React.ComponentProps<typeof TooltipContent>
  }
>(({ className, asChild = false, isActive = false, tooltip, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  const { isMobile, state } = useSidebar()

  const button = (
    <Comp
      ref={ref}
      className={cn(
        SIDEBAR_MENU_BUTTON_CLASS,
        isActive && 'bg-sidebar-accent-soft font-medium text-sidebar-accent-soft-foreground',
        className,
      )}
      {...props}
    />
  )

  if (!tooltip) return button

  const tooltipProps: React.ComponentProps<typeof TooltipContent> =
    typeof tooltip === 'string' ? { children: tooltip } : tooltip

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          {...tooltipProps}
        />
      </Tooltip>
    </TooltipProvider>
  )
})
SidebarMenuButton.displayName = 'SidebarMenuButton'

export const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { showIcon?: boolean }
>(({ className, showIcon = false, ...props }, ref) => {
  const { state } = useSidebar()
  if (state === 'collapsed' && !showIcon) return null

  return (
    <div ref={ref} className={cn('flex gap-2 px-2 py-1.5', className)} {...props}>
      {showIcon && <Skeleton className="h-4 w-4 rounded-md" />}
      <Skeleton className="h-4 flex-1 rounded-md" />
    </div>
  )
})
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton'

export const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5',
        className,
      )}
      {...props}
    />
  ),
)
SidebarMenuSub.displayName = 'SidebarMenuSub'

export const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ ...props }, ref) => <li ref={ref} {...props} />,
)
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem'

export const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<'a'> & {
    asChild?: boolean
    size?: 'sm' | 'md' | 'lg'
    isActive?: boolean
  }
>(({ className, asChild = false, size = 'md', isActive, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a'
  return (
    <Comp
      ref={ref}
      className={cn(
        SIDEBAR_MENU_SUB_BUTTON_CLASS,
        {
          'text-xs': size === 'sm',
          'text-sm': size === 'md',
          'text-base': size === 'lg',
        },
        isActive && 'bg-sidebar-accent-soft font-medium text-sidebar-accent-soft-foreground',
        className,
      )}
      {...props}
    />
  )
})
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton'
